// api/llmsService.ts - Cleaned version without ZIP
import { apiClient } from './apiClient';

// Types matching backend DTOs
export interface ExamineUrlDto {
    url: string;
}

export interface GenerateLlmsDto {
    url: string;
    maxUrls?: number;
    customInstructions?: string;
    selectedQueries?: string[];
    customRankingQuery?: string;
}

export interface VerifyLlmsDto {
    llmsUrl: string;
}

export interface RankingQuery {
    query: string;
    explanation: string;
    priority: number;
}

export interface WebsiteExamineResult {
    url: string;
    totalUrls: number;
    sampleUrls: string[];
    framework: string;
    title: string;
    description: string;
}

export interface QuerySuggestionsResult {
    url: string;
    websiteInfo: {
        title: string;
        description: string;
        framework: string;
    };
    suggestedQueries: RankingQuery[];
    pagesAnalyzed: number;
}

export interface LlmsGenerationResult {
    llmsTxt: string;
    installInstructions: string;
    framework: string;
    urlsProcessed: number;
    appliedQueries?: string[];
}

export interface LlmsVerificationResult {
    isValid: boolean;
    issues: string[];
    suggestions: string[];
    statusCode?: number;
}

export interface HealthStatus {
    status: string;
    timestamp: string;
    services: {
        exa: boolean;
        openai: boolean;
    };
    features?: {
        querySuggestions: boolean;
        rankingOptimization: boolean;
    };
}

export const llmsService = {
    pingUrl: async (url: string): Promise<{ reachable: boolean; status?: number; error?: string }> => {
        const normalizedUrl = llmsUtils.normalizeUrl(url);
        const response = await apiClient.post('/websites/ping', { url: normalizedUrl });
        return response.data;
    },

    examineWebsite: async (url: string): Promise<WebsiteExamineResult> => {
        const normalizedUrl = llmsUtils.normalizeUrl(url);
        const response = await apiClient.post('/llms/examine', { url: normalizedUrl });
        return response.data;
    },

    generateQuerySuggestions: async (url: string, signal?: AbortSignal): Promise<QuerySuggestionsResult> => {
        const normalizedUrl = llmsUtils.normalizeUrl(url);
        const response = await apiClient.post('/llms/suggest-queries', { url: normalizedUrl }, { signal });
        return response.data;
    },

    // Step 3: Generate llms.txt with optional ranking optimization
    generateLlms: async (data: GenerateLlmsDto): Promise<LlmsGenerationResult> => {
        const normalizedData = {
            ...data,
            url: llmsUtils.normalizeUrl(data.url)
        };
        const response = await apiClient.post('/llms/generate', normalizedData);
        return response.data;
    },

    // Step 4: Verify installation
    verifyLlms: async (llmsUrl: string): Promise<LlmsVerificationResult> => {
        const normalizedUrl = llmsUtils.normalizeUrl(llmsUrl);
        const response = await apiClient.post('/llms/verify', { llmsUrl: normalizedUrl });
        return response.data;
    },

    // Health check
    healthCheck: async (): Promise<HealthStatus> => {
        const response = await apiClient.get('/llms/health');
        return response.data;
    }
};

// Utility functions
export const llmsUtils = {
    downloadAsFile: (content: string, filename: string = 'llms.txt') => {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },

    formatFileSize: (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    normalizeUrl: (url: string): string => {
        if (!url) return url;

        const trimmedUrl = url.trim();

        if (/^https?:\/\//i.test(trimmedUrl)) {
            return trimmedUrl;
        }

        const urlWithoutWww = trimmedUrl.replace(/^www\./i, '');
        return `https://${urlWithoutWww}`;
    },

    isValidUrl: (url: string): boolean => {
        if (!url || url.trim().length === 0) {
            return false;
        }

        try {
            const normalizedUrl = llmsUtils.normalizeUrl(url.trim());
            const urlObj = new URL(normalizedUrl);

            if (!urlObj.hostname || urlObj.hostname.length === 0) {
                return false;
            }

            if (urlObj.hostname !== 'localhost' && !urlObj.hostname.includes('.')) {
                return false;
            }

            if (!['http:', 'https:'].includes(urlObj.protocol)) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    },

    displayUrl: (url: string): string => {
        try {
            const normalizedUrl = llmsUtils.normalizeUrl(url);
            const urlObj = new URL(normalizedUrl);
            return urlObj.hostname + (urlObj.pathname !== '/' ? urlObj.pathname : '');
        } catch {
            return url;
        }
    },

    formatFrameworkName: (framework: string): string => {
        const formatMap: { [key: string]: string } = {
            'nextjs': 'Next.js',
            'nestjs': 'NestJS',
            'fasthtml': 'FastHTML',
            'fastapi': 'FastAPI',
            'vue': 'Vue.js',
            'react': 'React',
            'angular': 'Angular',
            'svelte': 'Svelte',
            'django': 'Django',
            'flask': 'Flask',
            'express': 'Express.js'
        };
        return formatMap[framework.toLowerCase()] || framework.charAt(0).toUpperCase() + framework.slice(1);
    },

    copyToClipboard: async (text: string): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            const success = document.execCommand('copy');
            document.body.removeChild(textArea);
            return success;
        }
    },

    formatPriority: (priority: number): string => {
        const labels = ['', 'Low', 'Medium', 'Medium-High', 'High', 'Critical'];
        return labels[priority] || 'Unknown';
    },

    getPriorityColor: (priority: number): string => {
        const colors = {
            1: 'bg-gray-100 text-gray-700 border-gray-200',
            2: 'bg-blue-100 text-blue-700 border-blue-200',
            3: 'bg-yellow-100 text-yellow-700 border-yellow-200',
            4: 'bg-orange-100 text-orange-700 border-orange-200',
            5: 'bg-red-100 text-red-700 border-red-200'
        };
        return colors[priority as keyof typeof colors] || colors[3];
    },

    groupQueriesByPriority: (queries: RankingQuery[]): { [key: number]: RankingQuery[] } => {
        return queries.reduce((groups, query) => {
            const priority = query.priority;
            if (!groups[priority]) {
                groups[priority] = [];
            }
            groups[priority].push(query);
            return groups;
        }, {} as { [key: number]: RankingQuery[] });
    }
};